/**
 * Source-switch for the web Library: reads/writes the shared translations
 * ledger on the baba-v2 mobile gateway when it is reachable for the signed
 * -in user, and falls back to the existing web endpoints (apps/web/lib/
 * baba-api.ts via ../../src/store/babaApi.ts) exactly as today otherwise.
 *
 * Two separate questions, answered separately:
 *
 *  - "Is the gateway reachable?" decides where every translation is routed
 *    and which ledger the meters read. It is a one-row `GET /translations`
 *    with a timeout, shared module-wide (`probe`). It used to be the full
 *    500-row list with no timeout, so a slow download held the first
 *    translation hostage and one failure pinned the tab to the wrong ledger
 *    for good; a fallback is now re-checked in the background.
 *  - "What is on the ledger?" is the 500-row list, loaded only once a
 *    Library surface is mounted (`loadRows`), and patched locally after a
 *    translation instead of downloaded again.
 *
 * The fallback RTK Query hooks are never skipped while the gateway probe is
 * still in flight — only once it has CONFIRMED the gateway is reachable —
 * so a session the gateway rejects reads the fallback endpoints exactly as
 * before.
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import {
  useLibraryFavoritesQuery,
  useLibraryHistoryQuery,
  useRemoveFavoriteMutation,
  useRemoveHistoryEntryMutation,
  useSaveFavoriteMutation,
} from '../../src/store/babaApi'
import type { LangCode } from '../baba-api'
import {
  buildTextTranslationSyncRow,
  findLedgerRowForTranslation,
  mapGatewayRowsToFavorites,
  mapGatewayRowsToHistory,
  type GatewayTranslationRow,
  type LibraryEntry,
} from './gatewayLibraryMapping'
import {
  clearGatewayHistory,
  deleteGatewayTranslation,
  listGatewayTranslations,
  setGatewayTranslationFavorite,
  syncOneGatewayTranslationRow,
} from './gatewayLibraryClient'

// ---- shared gateway probe ----

export type GatewaySourceStatus = 'checking' | 'gateway' | 'fallback'

interface GatewaySourceState {
  status: GatewaySourceStatus
  /** The ledger, newest first, once a Library surface asked for it. Null
   *  means not loaded, which is normal: routing only needs `status`. */
  rows: GatewayTranslationRow[] | null
  rowsError: boolean
}

/** The routing check. Long enough for a slow phone network, short enough that
 *  a hung gateway never holds a translation for more than this. */
const PROBE_TIMEOUT_MS = 8_000
const ROWS_TIMEOUT_MS = 20_000
/** A fallback is re-checked (in the background) at most this often. */
const FALLBACK_RECHECK_MS = 60_000
const LEDGER_LIMIT = 500

let state: GatewaySourceState = { status: 'checking', rows: null, rowsError: false }
let probeInFlight: Promise<void> | null = null
let rowsInFlight: Promise<void> | null = null
let rowsRequestSeq = 0
let fallbackAt = 0
/** Mounted Library surfaces that render rows. */
let rowConsumers = 0
const listeners = new Set<() => void>()

function setState(next: GatewaySourceState): void {
  state = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): GatewaySourceState {
  return state
}

function timeoutSignal(ms: number): AbortSignal | undefined {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms)
  if (typeof AbortController === 'undefined') return undefined
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)
  return controller.signal
}

/**
 * Asks for ONE row, at most once concurrently. Success means the ledger is the
 * source of truth; ANY failure (signed-out `GatewayAuthError`, network error,
 * timeout, 5xx) falls back to the web endpoints. The gateway module itself is
 * defensive (see gatewayLibraryClient.ts), so every failure path lands here
 * rather than throwing past this probe.
 */
function probe(): Promise<void> {
  if (probeInFlight) return probeInFlight
  probeInFlight = listGatewayTranslations({ limit: 1, signal: timeoutSignal(PROBE_TIMEOUT_MS) })
    .then(() => {
      setState({ ...state, status: 'gateway' })
      if (rowConsumers > 0 && state.rows === null) void loadRows()
    })
    .catch(() => {
      fallbackAt = Date.now()
      setState({ status: 'fallback', rows: null, rowsError: false })
    })
    .finally(() => {
      probeInFlight = null
    })
  return probeInFlight
}

/** The ledger itself, for the Library surfaces. The newest request wins, so a
 *  reload started after a write never loses to an older one still in flight. */
function loadRows(force = false): Promise<void> {
  if (rowsInFlight && !force) return rowsInFlight
  const seq = ++rowsRequestSeq
  const request = listGatewayTranslations({ limit: LEDGER_LIMIT, signal: timeoutSignal(ROWS_TIMEOUT_MS) })
    .then((rows) => {
      if (seq === rowsRequestSeq) setState({ status: 'gateway', rows, rowsError: false })
    })
    .catch(() => {
      if (seq === rowsRequestSeq) setState({ ...state, rowsError: true })
    })
    .finally(() => {
      if (rowsInFlight === request) rowsInFlight = null
    })
  rowsInFlight = request
  return request
}

/**
 * After a gateway-mode write (save/toggle/delete/clear, a media translation):
 * reload the ledger if a Library surface is showing it, otherwise there is
 * nothing to refresh. Re-checks routing when not on the gateway.
 */
export function refreshGatewaySource(): Promise<void> {
  if (state.status !== 'gateway') return probe()
  if (rowConsumers > 0 || state.rows !== null) return loadRows(true)
  return Promise.resolve()
}

/**
 * A translation the gateway just recorded. Shown at the top of History at once
 * instead of downloading the whole ledger again after every translation.
 */
export function noteGatewayTranslationRow(row: Record<string, unknown>): void {
  if (state.status !== 'gateway' || state.rows === null) return
  const next = row as GatewayTranslationRow
  setState({ ...state, rows: [next, ...state.rows.filter((r) => r.id !== next.id)] })
}

/**
 * Resolves once the shared probe has a definite answer, starting one if none is
 * in flight yet (no added latency on repeat calls). Shared with
 * `apps/web/lib/translateGateway/client.ts` so the "route this text translation
 * through the gateway ledger" decision and the Library panels agree on "is the
 * gateway reachable" from the SAME probe instead of each running its own. A
 * fallback answers at once and is re-checked behind it, so the next request
 * can move back to the gateway without this one waiting.
 */
export async function ensureGatewaySourceProbe(): Promise<'gateway' | 'fallback'> {
  if (state.status === 'checking') await probe()
  else if (state.status === 'fallback' && Date.now() - fallbackAt >= FALLBACK_RECHECK_MS) void probe()
  return state.status === 'gateway' ? 'gateway' : 'fallback'
}

/** The current routing answer, without subscribing (event handlers, timers). */
export function gatewaySourceStatus(): GatewaySourceStatus {
  return state.status
}

/**
 * Reactive read of the shared probe's current status, for a consumer outside
 * the Library UI (e.g. the plan meter) that needs to know whether
 * translations are currently landing on the gateway ledger. Does not itself
 * start the probe: call `ensureGatewaySourceProbe()` (or mount any
 * `useLibrarySource` consumer) for that.
 */
export function useGatewaySourceStatus(): GatewaySourceStatus {
  return useSyncExternalStore(subscribe, getSnapshot).status
}

export function useIsGatewaySource(): boolean {
  return useGatewaySourceStatus() === 'gateway'
}

function useGatewaySourceState(enabled: boolean): GatewaySourceState {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot)
  useEffect(() => {
    if (!enabled) return
    rowConsumers += 1
    if (state.status === 'checking') void probe()
    else if (state.status === 'gateway' && state.rows === null && !rowsInFlight) void loadRows()
    else if (state.status === 'fallback' && Date.now() - fallbackAt >= FALLBACK_RECHECK_MS) void probe()
    return () => {
      rowConsumers -= 1
    }
  }, [enabled])
  return snapshot
}

// ---- public hook ----

export interface UseLibrarySourceOptions {
  /** Skip the gateway probe (and the fallback queries) entirely — e.g. while signed out. */
  enabled?: boolean
}

export interface LibraryListResult {
  data: LibraryEntry[] | undefined
  isLoading: boolean
  isError: boolean
  error: unknown
  refetch: () => void
}

export interface UseLibrarySourceResult {
  /** True once the gateway probe has CONFIRMED the ledger is reachable. */
  isGatewaySource: boolean
  /** True while the initial probe is still in flight. */
  isChecking: boolean
  favorites: LibraryListResult
  history: LibraryListResult
  /** Favorites a history entry (gateway: PATCH the row's `is_favorite`; fallback: POST a new favorites-table row, same as today). */
  saveFavorite: (entry: LibraryEntry) => Promise<void>
  /** Un-favorites (gateway: PATCH `is_favorite: false`, the row stays in History; fallback: deletes the favorites-table row, same as today). */
  removeFavorite: (id: string) => Promise<void>
  removeHistoryEntry: (id: string) => Promise<void>
  /** Clears every row on the account, on every device — not just the web's own
   * rows. No-op when not on the gateway source: the fallback backend has no
   * bulk-clear route. */
  clearHistory: () => Promise<void>
}

export function useLibrarySource(options: UseLibrarySourceOptions = {}): UseLibrarySourceResult {
  const enabled = options.enabled ?? true
  const gatewayState = useGatewaySourceState(enabled)
  const isGatewaySource = gatewayState.status === 'gateway'
  const isChecking = gatewayState.status === 'checking'

  const fallbackFavorites = useLibraryFavoritesQuery(undefined, { skip: !enabled || isGatewaySource })
  const fallbackHistory = useLibraryHistoryQuery(undefined, { skip: !enabled || isGatewaySource })
  const [removeFavoriteMutation] = useRemoveFavoriteMutation()
  const [removeHistoryMutation] = useRemoveHistoryEntryMutation()
  const [saveFavoriteMutation] = useSaveFavoriteMutation()

  const gatewayRows = gatewayState.status === 'gateway' ? gatewayState.rows : null
  const gatewayFavorites = useMemo(() => (gatewayRows ? mapGatewayRowsToFavorites(gatewayRows) : undefined), [gatewayRows])
  const gatewayHistory = useMemo(() => (gatewayRows ? mapGatewayRowsToHistory(gatewayRows) : undefined), [gatewayRows])

  const gatewayRefetch = useCallback(() => {
    void loadRows(true)
  }, [])

  // Routing is confirmed before the ledger itself has arrived: that gap is a
  // load, and a failed ledger download is an error with a retry, never a
  // silent switch to the other ledger.
  const gatewayLoading = gatewayRows === null && !gatewayState.rowsError
  const gatewayError = gatewayState.rowsError ? new Error('Could not load your translations') : null

  const favorites: LibraryListResult = isGatewaySource
    ? { data: gatewayFavorites, isLoading: gatewayLoading, isError: gatewayState.rowsError, error: gatewayError, refetch: gatewayRefetch }
    : {
        data: fallbackFavorites.data,
        isLoading: fallbackFavorites.isLoading,
        isError: fallbackFavorites.isError,
        error: fallbackFavorites.error,
        refetch: fallbackFavorites.refetch,
      }

  const history: LibraryListResult = isGatewaySource
    ? { data: gatewayHistory, isLoading: gatewayLoading, isError: gatewayState.rowsError, error: gatewayError, refetch: gatewayRefetch }
    : {
        data: fallbackHistory.data,
        isLoading: fallbackHistory.isLoading,
        isError: fallbackHistory.isError,
        error: fallbackHistory.error,
        refetch: fallbackHistory.refetch,
      }

  const saveFavorite = useCallback(
    async (entry: LibraryEntry) => {
      if (isGatewaySource) {
        await setGatewayTranslationFavorite(entry.id, true)
        await refreshGatewaySource()
        return
      }
      await saveFavoriteMutation({
        source_text: entry.source_text,
        translated_text: entry.translated_text,
        source_lang: entry.source_lang as LangCode,
        target_lang: entry.target_lang as LangCode,
      }).unwrap()
    },
    [isGatewaySource, saveFavoriteMutation],
  )

  const removeFavorite = useCallback(
    async (id: string) => {
      if (isGatewaySource) {
        await setGatewayTranslationFavorite(id, false)
        await refreshGatewaySource()
        return
      }
      await removeFavoriteMutation(id).unwrap()
    },
    [isGatewaySource, removeFavoriteMutation],
  )

  const removeHistoryEntry = useCallback(
    async (id: string) => {
      if (isGatewaySource) {
        await deleteGatewayTranslation(id)
        await refreshGatewaySource()
        return
      }
      await removeHistoryMutation(id).unwrap()
    },
    [isGatewaySource, removeHistoryMutation],
  )

  const clearHistory = useCallback(async () => {
    if (!isGatewaySource) return
    // Account-wide: no platform argument. One shared ledger cannot be
    // half-cleared without the other surface looking broken, and the button
    // says so ("Clear History on All Devices").
    await clearGatewayHistory()
    await refreshGatewaySource()
  }, [isGatewaySource])

  return {
    isGatewaySource,
    isChecking,
    favorites,
    history,
    saveFavorite,
    removeFavorite,
    removeHistoryEntry,
    clearHistory,
  }
}

// ---- favoriting a translation the translator is holding ----

/**
 * Patch one row inside the shared snapshot and notify, so History and the
 * Library repaint on the click instead of after a 500-row refetch.
 */
function patchRowLocally(id: string, patch: Partial<GatewayTranslationRow>): void {
  if (state.status !== 'gateway' || state.rows === null) return
  const index = state.rows.findIndex((row) => row.id === id)
  if (index < 0) return
  const rows = state.rows.slice()
  rows[index] = { ...rows[index], ...patch }
  setState({ ...state, rows })
}

/** Same, for a row that did not exist in the ledger before this write. */
function prependRowLocally(row: GatewayTranslationRow): void {
  noteGatewayTranslationRow(row as unknown as Record<string, unknown>)
}

/** The ledger rows to search when favoriting, loading them once if no Library
 *  surface has yet (the translator alone never needs them otherwise). */
async function rowsForLookup(): Promise<GatewayTranslationRow[]> {
  if (state.status === 'gateway' && state.rows === null) await loadRows()
  return state.status === 'gateway' && state.rows ? state.rows : []
}

export interface FavoriteGatewayTranslationParams {
  /** Ledger id of the row this translation was written as, when the caller knows it. */
  knownId?: string | null
  sourceText: string
  translatedText: string
  sourceLang: string
  targetLang: string
  mode?: string
  model?: string
  gender?: string
}

export interface FavoriteGatewayTranslationResult {
  /** 'patched' — an existing row was flagged; 'created' — the translation was
   *  not on the ledger and one row was written; 'unavailable' — not on the
   *  gateway source, so the caller keeps its own (apps/api) favorites path. */
  outcome: 'patched' | 'created' | 'unavailable'
  id?: string
}

/**
 * Favorite a translation the translator is holding, WITHOUT adding a second
 * History row for it.
 *
 * The bug this exists for: the old path pushed a fresh `POST /sync` row
 * whenever it could not name the ledger id (a result restored from
 * localStorage, an entry reopened from History, a translation whose push
 * landed after the click), so saving a translation made it reappear in
 * History as if it had just been translated again. The ledger already holds
 * the row — find it by its text and PATCH `is_favorite` instead. A row is
 * only written when the translation genuinely is not there.
 */
export async function favoriteGatewayTranslation(
  params: FavoriteGatewayTranslationParams,
): Promise<FavoriteGatewayTranslationResult> {
  const source = await ensureGatewaySourceProbe()
  if (source !== 'gateway') return { outcome: 'unavailable' }

  const rows = await rowsForLookup()
  const existing = findLedgerRowForTranslation(rows, {
    knownId: params.knownId,
    sourceText: params.sourceText,
    translatedText: params.translatedText,
  })

  const id = existing?.id ?? params.knownId ?? null
  if (id) {
    patchRowLocally(id, { is_favorite: true })
    try {
      await setGatewayTranslationFavorite(id, true)
      return { outcome: 'patched', id }
    } catch (err) {
      // A browser cannot always PATCH: the gateway's preflight has to allow
      // the method, and older Workers answer OPTIONS with GET/POST/DELETE
      // only. `POST /sync` is allowed everywhere and upserts BY ID, so
      // re-sending this row with is_favorite set is the same edit — not a
      // second row — and its own created_at keeps it where it is in History.
      if (existing) {
        const resynced = await syncOneGatewayTranslationRow({ ...existing, is_favorite: true }).catch(
          () => false,
        )
        if (resynced) return { outcome: 'patched', id }
      }
      patchRowLocally(id, { is_favorite: existing?.is_favorite ?? false })
      throw err
    }
  }

  const newId = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : null
  if (!newId) return { outcome: 'unavailable' }
  const row = buildTextTranslationSyncRow({
    id: newId,
    sourceText: params.sourceText,
    translatedText: params.translatedText,
    sourceLang: params.sourceLang,
    targetLang: params.targetLang,
    mode: params.mode,
    model: params.model,
    gender: params.gender,
    isFavorite: true,
  })
  const synced = await syncOneGatewayTranslationRow(row)
  if (!synced) return { outcome: 'unavailable' }
  prependRowLocally(row as GatewayTranslationRow)
  return { outcome: 'created', id: newId }
}

/**
 * The other half of the translator's bookmark: take a translation OFF the
 * saved list without leaving the page it was saved from. Same row, same
 * fallbacks — the row itself stays in History either way, which is what the
 * Library's own toggle does (`is_favorite: false`, never a delete).
 */
export async function unfavoriteGatewayTranslation(
  params: FavoriteGatewayTranslationParams,
): Promise<FavoriteGatewayTranslationResult> {
  const source = await ensureGatewaySourceProbe()
  if (source !== 'gateway') return { outcome: 'unavailable' }

  const rows = await rowsForLookup()
  const existing = findLedgerRowForTranslation(rows, {
    knownId: params.knownId,
    sourceText: params.sourceText,
    translatedText: params.translatedText,
  })
  const id = existing?.id ?? params.knownId ?? null
  // Nothing on the ledger to un-favorite: the caller's own favorites table is
  // the only place this can live, so let it handle the removal.
  if (!id) return { outcome: 'unavailable' }

  patchRowLocally(id, { is_favorite: false })
  try {
    await setGatewayTranslationFavorite(id, false)
    return { outcome: 'patched', id }
  } catch (err) {
    if (existing) {
      const resynced = await syncOneGatewayTranslationRow({ ...existing, is_favorite: false }).catch(
        () => false,
      )
      if (resynced) return { outcome: 'patched', id }
    }
    patchRowLocally(id, { is_favorite: existing?.is_favorite ?? true })
    throw err
  }
}
